import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { RefreshCw, Users, AlertTriangle, Inbox, Clock, Search } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Spinner } from '../ui/spinner';
import BrandSelect from '../ui/BrandSelect';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';
import { formatOwnerLabel } from './ownerDisplay';
import { resolveCurrentUserIds } from './currentUserIds';
import { isAssignedToMe } from './workItemQueueFilters';
import { formatSnapshotTime } from './formatSnapshotTime';
import { formatLastActivity, formatLastActivityAbsolute } from './formatLastActivity';
import {
  attentionReason,
  formatCompletenessCell,
  needsAttention,
  resolveStatusBadge,
} from './operationalStatus';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const reportStatusLabel = {
  not_on_platform: 'Not on platform',
  not_started: 'Not started',
  in_progress: 'In progress',
  generated: 'Generated',
};

const formatYoy = (yoy) => {
  if (!yoy || yoy.deltaPct == null) {
    if (yoy?.basis === 'first_year') return 'First year';
    return 'Not computed';
  }
  const sign = yoy.deltaPct > 0 ? '+' : '';
  const value = Number(yoy.deltaPct).toFixed(1);
  return `${sign}${value}%`;
};

const reportYearLabel = (row) =>
  row.reportingPeriodLabel || row.yoy?.reportingPeriodLabel || '—';

export { needsAttention, attentionReason, formatCompletenessCell, resolveStatusBadge };

/**
 * Surface A — consultant operational dashboard.
 */
const OperationalDashboard = ({
  dashboard,
  loading = false,
  ownerFilter = null,
  onOwnerFilterChange,
  onRefresh,
  profileDetails,
  organisationUsers,
}) => {
  const history = useHistory();
  const [attentionOnly, setAttentionOnly] = useState(false);
  const [nameQuery, setNameQuery] = useState('');
  const [queueMode, setQueueMode] = useState('me');

  const clients = dashboard?.clients || [];
  const teamFilter = dashboard?.teamFilter || { wholeFirm: 0, unassigned: 0, byMember: [] };
  const firmSummary = dashboard?.firmSummary || {};
  const queues = dashboard?.queues || { waitingOnMe: [], waitingOnUs: [], waitingOnClients: [], unallocated: [] };
  const computedAt = dashboard?.computedAt || dashboard?.firm?.computedAt || null;

  const { all: myUserIds } = useMemo(
    () =>
      resolveCurrentUserIds(profileDetails, [
        ...(organisationUsers || []),
        ...(teamFilter.byMember || []),
      ]),
    [profileDetails, organisationUsers, teamFilter.byMember],
  );

  const waitingOnUs = queues.waitingOnUs || [];
  const waitingOnMe = useMemo(() => {
    if (myUserIds.length) return waitingOnUs.filter((item) => isAssignedToMe(item, myUserIds));
    return queues.waitingOnMe || [];
  }, [queues.waitingOnMe, waitingOnUs, myUserIds]);

  const attentionCount = useMemo(() => clients.filter(needsAttention).length, [clients]);

  const ownerOptions = useMemo(() => {
    const opts = [
      { value: '', label: `Whole firm (${teamFilter.wholeFirm || 0})` },
      { value: 'unassigned', label: `No account manager (${teamFilter.unassigned || 0})` },
    ];
    (teamFilter.byMember || []).forEach((m) => {
      const name = formatOwnerLabel(m);
      opts.push({
        value: m.ownerUserId,
        label: `${name} (${m.clientCount})`,
      });
    });
    return opts;
  }, [teamFilter]);

  const displayRows = useMemo(() => {
    const q = nameQuery.trim().toLowerCase();
    return clients.filter((c) => {
      if (attentionOnly && !needsAttention(c)) return false;
      if (!q) return true;
      const name = String(c.name || '').toLowerCase();
      const id = String(c.clientOrgId || '').toLowerCase();
      return name.includes(q) || id.includes(q);
    });
  }, [clients, attentionOnly, nameQuery]);

  const selectedOwner = ownerOptions.find((o) => o.value === (ownerFilter || '')) || ownerOptions[0];

  return (
    <TooltipProvider>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <div>
            <h1 className='text-xl font-semibold text-foreground'>Consultant Dashboard</h1>
            <p className='text-sm text-muted-foreground'>
              Portfolio operations · updated {formatSnapshotTime(computedAt)}
            </p>
          </div>
          <div className='flex gap-2'>
            <Button variant='outline' size='sm' onClick={onRefresh} disabled={loading}>
              <RefreshCw size={16} strokeWidth={1.5} />
              Refresh
            </Button>
          </div>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
          <Card className='bg-card'>
            <CardContent className='p-4'>
              <p className='text-2xl font-bold'>{firmSummary.clientCount ?? clients.length}</p>
              <p className='text-xs text-muted-foreground'>Clients</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4'>
              <p className='text-2xl font-bold'>{firmSummary.generatedCount ?? 0}</p>
              <p className='text-xs text-muted-foreground'>Generated reports</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4'>
              <p className='text-2xl font-bold'>{firmSummary.inactiveCount ?? 0}</p>
              <p className='text-xs text-muted-foreground'>Inactive ({dashboard?.inactiveThresholdDays || 14}d)</p>
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardContent className='p-4'>
              <p className='text-2xl font-bold'>{firmSummary.unassignedCount ?? 0}</p>
              <p className='text-xs text-muted-foreground'>No account manager</p>
            </CardContent>
          </Card>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          <div className='min-w-[240px] flex-1'>
            <BrandSelect
              styles={adminToolbarSelect}
              options={ownerOptions}
              value={selectedOwner}
              onChange={(opt) => onOwnerFilterChange?.(opt?.value || null)}
              placeholder='Filter by account manager'
              isSearchable
            />
          </div>
          <div className='relative min-w-[200px] w-full sm:w-64'>
            <Search
              size={14}
              strokeWidth={1.5}
              className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none'
            />
            <Input
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              placeholder='Search by name'
              className='pl-8'
              aria-label='Search clients by name'
            />
          </div>
          <Button
            variant={attentionOnly ? 'default' : 'outline'}
            size='sm'
            onClick={() => setAttentionOnly((v) => !v)}>
            <AlertTriangle size={14} strokeWidth={1.5} />
            Need attention ({attentionCount})
          </Button>
        </div>

        <div className='flex items-center justify-between'>
          <div className='flex gap-2'>
            <Button
              size='sm'
              variant={queueMode === 'me' ? 'default' : 'outline'}
              onClick={() => setQueueMode('me')}>
              Waiting on me
            </Button>
            <Button
              size='sm'
              variant={queueMode === 'us' ? 'default' : 'outline'}
              onClick={() => setQueueMode('us')}>
              Waiting on us
            </Button>
          </div>
          <Button size='sm' variant='outline' onClick={() => history.push('/consultant/tasks')}>
            Open tasks
          </Button>
        </div>

        <div className='grid md:grid-cols-3 gap-3'>
          <Card className='bg-card'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm flex items-center gap-2'>
                <Clock size={16} strokeWidth={1.5} />
                {queueMode === 'me' ? 'Waiting on me' : 'Waiting on us'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(queueMode === 'me' ? waitingOnMe : waitingOnUs).length === 0 ? (
                <p className='text-sm text-muted-foreground'>{queues.note || 'Nothing waiting'}</p>
              ) : (
                <ul className='text-sm space-y-1'>
                  {(queueMode === 'me' ? waitingOnMe : waitingOnUs).map((t) => (
                    <li key={t.id} className='flex justify-between gap-2'>
                      <button
                        type='button'
                        className='text-left text-primary hover:underline'
                        onClick={() => history.push(`/consultant/tasks/${t.id}`)}>
                        {t.title || t.subject || t.id}
                      </button>
                      <span className='text-xs text-muted-foreground shrink-0'>{t.status || 'Open'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm flex items-center gap-2'>
                <Inbox size={16} strokeWidth={1.5} />
                Waiting on clients
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(queues.waitingOnClients || []).length === 0 ? (
                <p className='text-sm text-muted-foreground'>Nothing waiting</p>
              ) : (
                <ul className='text-sm space-y-1'>
                  {queues.waitingOnClients.map((t) => (
                    <li key={t.id} className='flex justify-between gap-2'>
                      <button
                        type='button'
                        className='text-left text-primary hover:underline'
                        onClick={() => history.push(`/consultant/tasks/${t.id}`)}>
                        {t.title || t.subject || t.id}
                      </button>
                      <span className='text-xs text-muted-foreground shrink-0'>{t.status || 'Waiting'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
          <Card className='bg-card'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm flex items-center gap-2'>
                <Users size={16} strokeWidth={1.5} />
                Unallocated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className='text-2xl font-bold mb-2'>{queues.unallocatedCount ?? (queues.unallocated || []).length}</p>
              {(queues.unallocated || []).length === 0 ? (
                <p className='text-sm text-muted-foreground'>Nothing waiting</p>
              ) : (
                <ul className='text-sm space-y-1'>
                  {(queues.unallocated || []).slice(0, 5).map((t) => (
                    <li key={t.id}>
                      <button
                        type='button'
                        className='text-left text-primary hover:underline'
                        onClick={() => history.push(`/consultant/tasks/${t.id}`)}>
                        {t.title || t.id}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className='bg-card'>
          <CardHeader className='pb-2'>
            <CardTitle className='text-sm flex items-center gap-2'>
              <Users size={16} strokeWidth={1.5} />
              Clients &amp; progress
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && clients.length === 0 ? (
              <div className='flex justify-center py-8'>
                <Spinner />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className='border-border'>
                    <TableHead>Client</TableHead>
                    <TableHead>Last activity</TableHead>
                    <TableHead>Report year</TableHead>
                    <TableHead>Completeness</TableHead>
                    <TableHead>YoY</TableHead>
                    <TableHead>Report</TableHead>
                    <TableHead>Account manager</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayRows.map((row) => {
                    const badge = resolveStatusBadge(row);
                    const reason = attentionOnly ? attentionReason(row) : null;
                    return (
                      <TableRow
                        key={row.clientOrgId}
                        className={`border-border cursor-pointer hover:bg-muted/50 ${
                          badge.key === 'access_blocked' ||
                          badge.key === 'inactive' ||
                          badge.key === 'access_unknown'
                            ? 'opacity-70'
                            : ''
                        }`}
                        onClick={() => history.push(`/consultant/client/${row.clientOrgId}/health`)}>
                        <TableCell className='font-medium text-foreground'>
                          <div>{row.name || 'Unnamed client'}</div>
                          {reason && (
                            <div className='text-xs text-muted-foreground mt-0.5'>{reason}</div>
                          )}
                        </TableCell>
                        <TableCell
                          className='text-muted-foreground text-xs'
                          title={formatLastActivityAbsolute(row.lastActiveAt)}>
                          {formatLastActivity(row.lastActiveAt)}
                        </TableCell>
                        <TableCell className='text-muted-foreground text-xs'>{reportYearLabel(row)}</TableCell>
                        <TableCell className='text-muted-foreground'>{formatCompletenessCell(row)}</TableCell>
                        <TableCell>{formatYoy(row.yoy)}</TableCell>
                        <TableCell>
                          <Badge variant='secondary'>
                            {reportStatusLabel[row.reportStatus] || 'Not computed'}
                          </Badge>
                        </TableCell>
                        <TableCell className='text-muted-foreground text-xs'>{formatOwnerLabel(row)}</TableCell>
                        <TableCell>
                          {badge.lockReason ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span>
                                  <Badge className={badge.className}>{badge.label}</Badge>
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className='bg-popover text-popover-foreground border-border max-w-xs'>
                                {badge.lockReason}
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <Badge className={badge.className}>{badge.label}</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {displayRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className='text-center text-muted-foreground py-6'>
                        No clients in this filter.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default OperationalDashboard;
